import { AxiosResponse } from 'axios';
import { runInAction } from 'mobx';

import { notUndefined } from 'ui/base/types';

import { Optional, ResultKind } from 'services/base/types';
import { createRequestString, getResponseBytes, http, isHttpError } from 'services/base/http';

import {
  IPangolinDomain,
  UpdateSnapshot,
  UpdateSnapshotMap,
} from './types';

import {
  UpdateSnapshotObject as UpdateSnapshotProto,

  GetUpdateSnapshotResponse,
  GetUpdateSnapshotsResponse,
  DeleteUpdateSnapshotRequest,
} from './protos/update_pb';

export type DeleteUpdateSnapshotResult =
  | { kind: ResultKind.OK }
  | { kind: ResultKind.SYSTEM_ERROR; status?: number }
  | { kind: ResultKind.USER_ERROR; status?: number };

export class UpdateSnapshotService {
  static async fetch(domain: IPangolinDomain, id: string, save = true): Promise<Optional<UpdateSnapshot>> {
    const httpResp = await http.get(`/pangolin/${domain.id}/update/snapshots/${id}`);
    return runInAction(() => {
      const remoteSnapshot = parseUpdateSnapshotResponse(httpResp);
      if (remoteSnapshot == null) return undefined;

      if (save) {
        const localSnapshot = domain.updateSnapshots.get(id);
        if (localSnapshot == null) {
          domain.updateSnapshots.set(remoteSnapshot.id, remoteSnapshot);
          domain.updateSnapshots.sort(snapshotSorter);
        } else {
          Object.assign(localSnapshot, remoteSnapshot);
        }
      }

      return remoteSnapshot;
    });
  }

  static async fetchAll(domain: IPangolinDomain, save = true): Promise<UpdateSnapshotMap> {
    const httpResp = await http.get(`/pangolin/${domain.id}/update/snapshots`);
    const remoteSnapshots = parseUpdateSnapshotsResponse(httpResp);
    return runInAction(() => {
      if (save) domain.updateSnapshots = remoteSnapshots;
      return remoteSnapshots;
    });
  }

  static async delete(domain: IPangolinDomain, snapshot: UpdateSnapshot, password: string): Promise<DeleteUpdateSnapshotResult> {
    const request = createDeleteUpdateSnapshotRequest(password);
    try {
      const url = `/pangolin/${domain.id}/update/snapshots/${snapshot.id}`;
      await http.delete(url, { data: createRequestString(request) });
      return runInAction(() => {
        domain.updateSnapshots.delete(snapshot.id);
        return { kind: ResultKind.OK };
      });
    } catch (error) {
      const status = isHttpError(error) ? error.response?.status : undefined;
      const kind = status && status < 500 ? ResultKind.USER_ERROR : ResultKind.SYSTEM_ERROR;
      return { kind, status };
    }
  }
}

function parseUpdateSnapshotResponse(httpResp: AxiosResponse): Optional<UpdateSnapshot> {
  const rawBytes = getResponseBytes(httpResp);
  const rootProto = GetUpdateSnapshotResponse.deserializeBinary(rawBytes);
  const itemProto = rootProto.getSnapshot();
  return snapshotFromProto(itemProto);
}

function parseUpdateSnapshotsResponse(httpResp: AxiosResponse): UpdateSnapshotMap {
  const rawBytes = getResponseBytes(httpResp);
  const rootProto = GetUpdateSnapshotsResponse.deserializeBinary(rawBytes);
  const listProto = rootProto.getSnapshotsList();
  const snapshotObjects = listProto.map(snapshotFromProto).filter(notUndefined);
  return new UpdateSnapshotMap(snapshotObjects.map((s) => [s.id, s]));
}

function snapshotFromProto(proto: Optional<UpdateSnapshotProto>): Optional<UpdateSnapshot> {
  if (proto == null) return undefined;

  const id = proto.getId();
  const release_id = proto.getReleaseId();
  const is_enabled = proto.getIsEnabled();
  const created_at = proto.getCreatedAt();
  const updated_at = proto.getUpdatedAt();

  if (id === undefined) return undefined;
  if (release_id === undefined) return undefined;
  if (is_enabled === undefined) return undefined;
  if (created_at === undefined) return undefined;
  if (updated_at === undefined) return undefined;

  return {
    id, release_id, is_enabled,
    created_at: created_at.toDate(),
    updated_at: updated_at.toDate(),
    size: proto.getSize(),
  };
}

function snapshotSorter(a: UpdateSnapshot, b: UpdateSnapshot): number {
  const aCreatedAt = a.created_at.getTime();
  const bCreatedAt = b.created_at.getTime();
  return aCreatedAt - bCreatedAt;
}

function createDeleteUpdateSnapshotRequest(password: string): DeleteUpdateSnapshotRequest {
  const proto = new DeleteUpdateSnapshotRequest();
  proto.setPassword(password);
  return proto;
}
